"use client"

import {
    ColumnDef,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table"
import { V2ProjectId } from "@wise/identities"
import { useMemo, useState } from "react"
import { ProjectOptions } from "./ProjectOptions"
import { ProjectStatusIndicator } from "./ProjectStatusIndicator"
import { GenericTable } from "../../../components/Table/GenericTable"
import { interpolateTemplate } from "../../helpers/interpolateTemplate"
import { Collaborators } from "../../users/components/Collaborators"
import { ProjectV2 } from "../data/getProjectsV2"
import { getProjectStatus, ProjectStatus } from "../helpers/getProjectStatus"
import { ActiveRegion, regions } from "@wise/regions"

interface Props {
    projects: ProjectV2[]
    showProjectOptions?: boolean
    linkTemplate?: string
}

type Record = {
    id: string
    name: string
    region: ActiveRegion
    collaborators: {
        id: string
        name: string
        picture: string
    }[]
    createdAt: Date
    status: ProjectStatus
}

const columnHelper = createColumnHelper<Record>()

const defaultHrefGenerator = (projectId: V2ProjectId) => {
    return `/data-analyzer/${projectId.region}/${projectId.id}`
}

export default function ProjectsTableV2({ linkTemplate, projects, showProjectOptions = true }: Props) {
    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "createdAt",
            desc: true,
        },
    ])

    const hrefGenerator = useMemo(() => {
        if (linkTemplate) {
            const generator = (projectId: V2ProjectId) =>
                interpolateTemplate(linkTemplate, {
                    projectRegion: projectId.region,
                    projectId: projectId.id,
                    organizationId: projectId.organizationId.id,
                })
            return generator
        }
        return defaultHrefGenerator
    }, [linkTemplate])

    const columns = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cols: ColumnDef<Record, any>[] = [
            columnHelper.accessor((row) => `${row.name}::::${row.status}`, {
                id: "name",
                header: ({ column }) => (
                    <div className="px-3">
                        <input
                            type="text"
                            value={(column.getFilterValue() ?? "") as string}
                            onChange={(e) => column.setFilterValue(e.target.value)}
                            placeholder={`Search by project name`}
                            className="focus:border-primary-600 focus:ring-primary-600 block w-full rounded-md border-gray-300 text-sm placeholder:text-gray-400 dark:border-zinc-700 dark:bg-zinc-800"
                        />
                    </div>
                ),
                cell: ({ row }) => {
                    const projectId = V2ProjectId.fromUrl(row.id)
                    const nameStatusDividerPosition = (row.getValue("name") as string).lastIndexOf("::::")
                    const name = (row.getValue("name") as string).slice(0, nameStatusDividerPosition)
                    const status = (row.getValue("name") as string).slice(nameStatusDividerPosition + 4)
                    return (
                        <>
                            <a
                                href={hrefGenerator(projectId)}
                                className="text-primary-600 hover:text-primary-900 hover:underline"
                            >
                                {name}
                                <ProjectStatusIndicator status={status} />
                            </a>
                            <dl className="flex flex-row items-center gap-3 font-normal lg:hidden">
                                <dt className="sr-only">Collaborators</dt>
                                <dd className="min-w-12 lg:min-w-auto">
                                    <Collaborators users={row.getValue("collaborators")} />
                                </dd>
                                <span>&middot;</span>
                                <dt className="sr-only">Region</dt>
                                <dd>{regions[projectId.region].flag}</dd>
                                <span>&middot;</span>
                                <dt className="sr-only">Created at</dt>
                                <dd className="mt-1 truncate text-gray-700 dark:text-gray-400">
                                    {
                                        <time dateTime={row.getValue<Date>("createdAt").toISOString()}>
                                            {row.getValue<Date>("createdAt").toLocaleDateString(undefined, {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                            })}
                                        </time>
                                    }
                                </dd>
                            </dl>
                        </>
                    )
                },
            }),
            columnHelper.accessor("collaborators", {
                header: "Collaborators",
                cell: ({ row }) => <Collaborators users={row.getValue("collaborators")} />,
                meta: {
                    className: "hidden lg:table-cell",
                },
            }),
            columnHelper.accessor("region", {
                header: "Region",
                cell: ({ row }) => {
                    const projectId = V2ProjectId.fromUrl(row.id)
                    return (
                        <div className="flex items-center gap-2">
                            <span title={regions[projectId.region].label} aria-hidden="true">
                                {regions[projectId.region].flag}
                            </span>
                            <span className="sr-only">{regions[projectId.region].label}</span>
                        </div>
                    )
                },
                meta: {
                    className: "hidden lg:table-cell",
                },
            }),
            columnHelper.accessor("createdAt", {
                header: "Created",
                cell: ({ row }) => (
                    <time
                        className="text-gray-700 dark:text-gray-400"
                        dateTime={row.getValue<Date>("createdAt").toISOString()}
                    >
                        {row.getValue<Date>("createdAt").toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                        })}
                    </time>
                ),
                meta: {
                    className: "hidden lg:table-cell",
                },
            }),
        ]

        if (showProjectOptions) {
            cols.push(
                columnHelper.display({
                    id: "actions",
                    cell: (props) => <ProjectOptions projectId={props.row.id} />,
                })
            )
        }

        return cols
    }, [hrefGenerator, showProjectOptions])

    const data: Record[] = useMemo(
        () =>
            projects.map((p) => ({
                id: p.id,
                region: p.region,
                name: p.name,
                collaborators: p.users.map((u) => ({
                    id: u.userId,
                    name: u.name ?? "",
                    picture: u.picture ?? `https://ui-avatars.com/api/?name=${u.name}`,
                })),
                createdAt: new Date(p.createdAt),
                status: getProjectStatus(p.events),
            })),
        [projects]
    )

    const table = useReactTable({
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        initialState: {
            pagination: {
                pageSize: 12,
            },
            sorting: [
                {
                    id: "createdAt",
                    desc: true,
                },
            ],
        },
        data,
        getRowId: (row) => `${row.id}`,
    })

    return <GenericTable table={table} />
}
